import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, ArrowRightLeft, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

export interface ReviewSubmission {
  id: string;
  full_name: string;
  email: string | null;
  flag_unexpected?: boolean | null;
  flag_duplicate_of?: string | null;
  review_status?: string | null;
  submitted_at?: string;
}

interface Props {
  leadId: string;
  submissions: ReviewSubmission[];
  /** Programme title where the same person also appears, keyed by submission id. */
  duplicateLabel: (s: ReviewSubmission) => string | null;
  onMove: (s: ReviewSubmission) => void;
}

export function needsReview(s: ReviewSubmission, hasDuplicate: boolean) {
  if ((s.review_status || "open") !== "open") return false;
  return !!s.flag_unexpected || !!s.flag_duplicate_of || hasDuplicate;
}

export function NeedsReviewCard({ leadId, submissions, duplicateLabel, onMove }: Props) {
  const qc = useQueryClient();

  const dismissMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any)
        .from("student_intake_submissions")
        .update({ review_status: "dismissed" })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["student-intake-subs", leadId] });
      toast.success("Marked as reviewed");
    },
    onError: (e: any) => toast.error(e?.message || "Could not update"),
  });

  const flagged = submissions.filter((s) => needsReview(s, !!duplicateLabel(s)));
  if (!flagged.length) return null;

  return (
    <Card className="border-amber-500/60">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 text-amber-700">
          <AlertTriangle className="h-4 w-4" /> Needs review ({flagged.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {flagged.map((s) => {
          const dup = duplicateLabel(s);
          return (
            <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-2">
              <div className="text-sm">
                <span className="font-medium">{s.full_name}</span>
                {s.email && <span className="text-muted-foreground ml-2 text-xs">{s.email}</span>}
                <div className="flex flex-wrap gap-1 mt-1">
                  {s.flag_unexpected && (
                    <Badge variant="outline" className="text-amber-600 border-amber-500 font-normal">
                      Not on the expected list
                    </Badge>
                  )}
                  {(dup || s.flag_duplicate_of) && (
                    <Badge variant="outline" className="text-amber-600 border-amber-500 font-normal">
                      Also on {dup || "another programme"}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" onClick={() => onMove(s)}>
                  <ArrowRightLeft className="h-4 w-4 mr-1" /> Move
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={dismissMut.isPending}
                  onClick={() => dismissMut.mutate(s.id)}
                >
                  {dismissMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
                  This is correct
                </Button>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
